import { type ClientDiagnostic, summarizeDiagnosticError } from '@everyturn/protocol';
import { API_BASE_URL } from './api-config';

const MAX_REPORTS = 60;
const MAX_PENDING = 10;

function createReportId(): string {
  // getRandomValues also works on supported LAN HTTP origins, unlike randomUUID.
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6]! & 0x0f) | 0x40;
  bytes[8] = (bytes[8]! & 0x3f) | 0x80;
  const hex = [...bytes].map((byte) => byte.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

export function createDiagnosticReporter(
  endpoint: string,
  clientVersion: string,
  send: typeof fetch = (...args) => fetch(...args),
) {
  const pending: ClientDiagnostic[] = [];
  let submitted = 0;
  let flushing: Promise<void> | undefined;
  let disposed = false;
  const lifetime = new AbortController();

  function flush(): Promise<void> {
    if (flushing) return flushing;
    if (disposed || pending.length === 0) return Promise.resolve();
    flushing = (async () => {
      while (!disposed && pending.length > 0) {
        const report = pending[0]!;
        try {
          const response = await send(endpoint, {
            method: 'POST',
            credentials: 'omit',
            referrerPolicy: 'no-referrer',
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify(report),
            signal: AbortSignal.any([lifetime.signal, AbortSignal.timeout(5000)]),
          });
          if (response.status !== 204) {
            console.warn('[Diagnostics] Report delivery rejected', report.reportId, response.status);
            if (response.status >= 400 && response.status < 500 && response.status !== 429) pending.shift();
            return;
          }
          pending.shift();
        } catch {
          if (!disposed)
            console.warn('[Diagnostics] Report not delivered; retained until a later connection', report.reportId);
          return;
        }
      }
    })().finally(() => {
      flushing = undefined;
    });
    return flushing;
  }

  function report(event: ClientDiagnostic['event'], error?: unknown, gameSessionId?: string | null): string {
    const reportId = createReportId();
    if (disposed || submitted >= MAX_REPORTS || pending.length >= MAX_PENDING) {
      console.warn('[Diagnostics] Report not queued: reporting stopped or capacity reached', reportId);
      return reportId;
    }
    submitted++;
    pending.push({
      event,
      reportId,
      clientVersion,
      occurredAt: new Date().toISOString(),
      ...(gameSessionId && /^gs_[a-f0-9-]{36}$/.test(gameSessionId) ? { gameSessionId } : {}),
      ...(error === undefined ? {} : { error: summarizeDiagnosticError(error) }),
    });
    void flush();
    return reportId;
  }

  return {
    report,
    flush,
    dispose() {
      disposed = true;
      lifetime.abort();
      pending.length = 0;
    },
  };
}

export const diagnostics = createDiagnosticReporter(
  `${API_BASE_URL.replace(/\/+$/, '')}/diagnostics`,
  import.meta.env.VITE_APP_VERSION ?? 'development',
);

export function supportReference(reportId: string): string {
  return `Support reference: ${reportId}. Share this reference and the approximate time with the staging operator, not screenshots of private cards.`;
}

export function installBrowserDiagnostics(): () => void {
  const onError = (event: ErrorEvent) => diagnostics.report('browser_error', event.error);
  const onRejection = (event: PromiseRejectionEvent) => diagnostics.report('unhandled_rejection', event.reason);
  const onOnline = () => void diagnostics.flush();
  window.addEventListener('error', onError);
  window.addEventListener('unhandledrejection', onRejection);
  window.addEventListener('online', onOnline);
  return () => {
    window.removeEventListener('error', onError);
    window.removeEventListener('unhandledrejection', onRejection);
    window.removeEventListener('online', onOnline);
    diagnostics.dispose();
  };
}
